import { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../contexts/userContext";
import { ICarrier } from "../interfaces/configuration";
import {
  IAcceptedIntegration,
  ISelectedIntegration,
} from "../interfaces/integrations";
import { IExportRequest } from "../interfaces/order";
import { IAlert } from "../interfaces/alert";
import { fetchData } from "../utils/dataProvider";
import { ERROR, GET, OWN_FLEET, OWN_FLEET_LABEL } from "../utils/constants";
import { isCaseInsensitiveEqual } from "../utils/text";
import { useSelectAliases } from "./useSelectAliases";
import { createOrdersExportStrategy } from "../strategies/ordersExportStrategyFactory";

const translateOwnFleetCarrier = (carriers: ICarrier[]) =>
  carriers.map((carrier) =>
    isCaseInsensitiveEqual(carrier.name, OWN_FLEET)
      ? { ...carrier, name: OWN_FLEET_LABEL.toUpperCase() }
      : carrier,
  );
const getClientIntegrations = async (): Promise<ISelectedIntegration[]> => {
  const { clients } = await fetchData({
    method: GET,
    url: `/internal/clients/accepted/?skip=${0}&limit=${0}`,
  });
  return clients.map((item: ISelectedIntegration) => ({
    id: item.id,
    name: item.name,
  }));
};

export const useExportOrdersStrategy = () => {
  const { selectedMarketplace, currentUser } = useContext(UserContext);
  const [carriers, setCarriers] = useState<ICarrier[]>([]);
  const [merchants, setMerchants] = useState<IAcceptedIntegration[]>([]);
  const [clients, setClients] = useState<ISelectedIntegration[]>([]);
  const [requests, setRequests] = useState<IExportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<IAlert>({} as IAlert);

  const strategy = useMemo(
    () => createOrdersExportStrategy(currentUser.entity, selectedMarketplace),
    [currentUser.entity, selectedMarketplace],
  );
  const { aliases } = strategy.shouldFetchAliases()
    ? useSelectAliases(strategy.shouldFetchAliases() ? selectedMarketplace : "")
    : { aliases: [] };

  const fetchCarriers = async () => {
    try {
      const response = await fetchData({
        url: strategy.getCarriersEndpoint(),
        method: GET,
      });
      setCarriers(translateOwnFleetCarrier(response));
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  const fetchClients = async () => {
    try {
      const clientIntegrations = strategy.shouldFetchClients() ? await getClientIntegrations() : [];
      setClients(clientIntegrations);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };
  const fetchMerchants = async () => {
    if (!strategy.shouldFetchMerchants()) return;
    try {
      const { integrations } = await fetchData({
        url: "/marketplaces_merchants/acceptedIntegrationsOfMarketplace",
        method: GET,
      });
      setMerchants(integrations);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  const fetchExportFiles = async () => {
    setLoading(true);
    try {
      const results = await fetchData({
        url: strategy.getFilesEndpoint(selectedMarketplace),
        method: GET,
      });
      // eslint-disable-next-line no-nested-ternary
      const parsedResults = Array.isArray(results)
        ? results
        : Array.isArray(results?.data)
          ? results.data
          : [];
      setRequests(parsedResults);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    } finally {
      setLoading(false);
    }
  };

  const initializeData = async () => {
    await Promise.all([fetchCarriers(), fetchMerchants(), fetchClients(), fetchExportFiles()]);
  };

  useEffect(() => {
    initializeData();
    setAlert({} as IAlert);
  }, [selectedMarketplace]);

  return {
    strategy,
    carriers,
    merchants,
    aliases: strategy.shouldFetchAliases() ? aliases : [],
    requests,
    loading,
    alert,
    setAlert,
    setRequests,
    refreshFiles: fetchExportFiles,
    selectedMarketplace,
    currentUser,
    clients,
  };
};
